import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import type { FallbackDataFetcher, FallbackFetchContext } from "../types";

interface SlackSearchMatch {
  type: string;
  text: string;
  user: string;
  username: string;
  ts: string;
  channel?: { id: string; name: string };
}

/**
 * Fetch recent Slack messages that have reactions via search.messages.
 * Used as a fallback for slack-new-reaction-added trigger.
 *
 * Note: Slack's search.messages API does NOT include the `reactions` array
 * in search results. The `has:reaction` query finds messages that were reacted
 * to, but we don't know which specific reactions. We construct a representative
 * trigger result for each message so users can test their workflow logic.
 *
 * Requires: Slack connector with user token (search:read scope).
 */
export const slackReactionFallback: FallbackDataFetcher = async (ctx: FallbackFetchContext) => {
  const { triggerPayload, connectorId } = ctx;

  if (!connectorId) return [];

  // If trigger has configured channels, filter results to those channels.
  const configuredChannels = triggerPayload.conversations as string[] | undefined;
  const channelFilter = configuredChannels && configuredChannels.length > 0 ? new Set(configuredChannels) : null;

  const response = await authenticatedFetch<{
    data: {
      result: {
        success: boolean;
        data: {
          ret: {
            ok: boolean;
            messages: {
              matches: SlackSearchMatch[];
            };
          };
        };
      };
    };
  }>(`${V2_ENDPOINT}/v1/actions/slack-find-message/test`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      inputs: {
        query:
          configuredChannels && configuredChannels.length > 0
            ? `has:reaction ${configuredChannels.map(id => `in:<#${id}>`).join(" ")}`
            : "has:reaction",
        count: 10,
        sort: "timestamp",
        sort_dir: "desc",
      },
      connector: { id: connectorId, type: "slack" },
    }),
  });

  if (!response.ok) return [];
  const json = await response.json();

  const allMessages = (json.data?.result?.data?.ret?.messages?.matches ?? []) as SlackSearchMatch[];
  if (allMessages.length === 0) return [];

  // Filter by configured channels if set.
  const messages = channelFilter
    ? allMessages.filter(msg => msg.channel?.id && channelFilter.has(msg.channel.id))
    : allMessages;
  if (messages.length === 0) return [];

  // Build trigger results from messages that had reactions.
  // We don't know the specific reaction (search API doesn't return it),
  // so we use a placeholder. The user picks a message and the workflow
  // runs with realistic channel/user/timestamp data.
  return messages.map(msg => {
    const channelId = msg.channel?.id ?? "unknown";
    const channelName = msg.channel?.name ?? "unknown";

    return {
      triggeredAt: new Date(parseFloat(msg.ts) * 1000).toISOString(),
      reaction: "thumbsup",
      user: { id: msg.user, username: msg.username },
      item: {
        type: "message",
        channel: channelId,
        ts: msg.ts,
      },
      event_ts: msg.ts,
      // Metadata for UI display
      _reactionName: "thumbsup",
      _reactionCount: 1,
      _messagePreview: msg.text?.slice(0, 120) ?? "",
      _userName: msg.username ?? msg.user,
      _channelName: channelName,
    };
  });
};
